import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Filter, RefreshCw, UserCheck, RotateCcw, X } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { adminDashboardService } from '../../services/adminDashboardService';

export default function AbidjanDeliveryIncidentsManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({
    agentId: '',
    incidentType: '',
    dateFrom: '',
    dateTo: ''
  });
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Incident type labels in French
  const incidentTypeLabels = {
    'ABSENT': 'Client absent',
    'ADRESSE_INCORRECTE': 'Adresse incorrecte',
    'REFUSE_LIVRAISON': 'Refus de livraison',
    'COLIS_ENDOMMAGE': 'Colis endommagé',
    'AUTRE': 'Autre incident'
  };

  useEffect(() => {
    loadIncidents();
    loadAgents();
  }, [filters]);

  const loadIncidents = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminDashboardService?.getIncidentDeliveryTasks(filters);
      setIncidents(data);
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement des incidents');
      console.error('Error loading incidents:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const data = await adminDashboardService?.getAgentsForFilter();
      setAgents(data);
    } catch (err) {
      console.error('Error loading agents:', err);
    }
  };

  const handleResolveIncident = async (incidentId) => {
    if (!window.confirm('Confirmer la résolution de cet incident et repasser en statut "À préparer" ?')) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);
      await adminDashboardService?.resolveIncident(incidentId);
      setSuccessMessage('Incident résolu avec succès - statut repassé à "À préparer"');
      await loadIncidents();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la résolution de l\'incident');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReassignIncident = async () => {
    if (!selectedIncident || !selectedAgent) {
      setError('Veuillez sélectionner un agent');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);
      await adminDashboardService?.reassignDeliveryTask(selectedIncident?.id, selectedAgent);
      setSuccessMessage('Livraison réassignée avec succès');
      setShowReassignModal(false);
      setSelectedIncident(null);
      setSelectedAgent('');
      await loadIncidents();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la réassignation');
    } finally {
      setActionLoading(false);
    }
  };

  const openReassignModal = (incident) => {
    setSelectedIncident(incident);
    setSelectedAgent('');
    setShowReassignModal(true);
  };

  const closeReassignModal = () => {
    setShowReassignModal(false);
    setSelectedIncident(null);
    setSelectedAgent('');
  };

  const getStatusBadgeClass = (status) => {
    const baseClasses = 'px-3 py-1 rounded-full text-sm font-semibold';
    if (status === 'INCIDENT') {
      return `${baseClasses} bg-red-100 text-red-800`;
    }
    return `${baseClasses} bg-gray-100 text-gray-800`;
  };

  const getIncidentTypeBadgeClass = (type) => {
    const baseClasses = 'px-2 py-1 rounded text-xs font-medium';
    const colors = {
      'ABSENT': 'bg-orange-100 text-orange-800',
      'ADRESSE_INCORRECTE': 'bg-yellow-100 text-yellow-800',
      'REFUSE_LIVRAISON': 'bg-red-100 text-red-800',
      'COLIS_ENDOMMAGE': 'bg-purple-100 text-purple-800',
      'AUTRE': 'bg-gray-100 text-gray-800'
    };
    return `${baseClasses} ${colors?.[type] || colors?.['AUTRE']}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-green-600 hover:text-green-700 font-semibold text-lg"
              >
                ADT Express
              </button>
              <span className="text-gray-300">|</span>
              <h1 className="text-xl font-bold text-gray-800">Incidents de Livraison - Abidjan</h1>
            </div>
            <AdminNavigation />
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
            <span>{successMessage}</span>
            <button onClick={() => setSuccessMessage(null)} className="text-green-600 hover:text-green-800">
              <X size={18} />
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center">
              <Filter className="mr-2" size={20} />
              Filtres
            </h2>
            <button
              onClick={loadIncidents}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              disabled={loading}
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              <span>Actualiser</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Agent Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent assigné</label>
              <select
                value={filters?.agentId}
                onChange={(e) => setFilters({ ...filters, agentId: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Tous les agents</option>
                {agents?.map(agent => (
                  <option key={agent?.id} value={agent?.id}>
                    {agent?.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Incident Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type d'incident</label>
              <select
                value={filters?.incidentType}
                onChange={(e) => setFilters({ ...filters, incidentType: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Tous les types</option>
                {Object.entries(incidentTypeLabels)?.map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
              <input
                type="date"
                value={filters?.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
              <input
                type="date"
                value={filters?.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Incidents Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <AlertTriangle className="mr-2 text-red-600" size={24} />
              Incidents de Livraison ({incidents?.length})
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="animate-spin text-green-600" size={32} />
              <span className="ml-3 text-gray-600">Chargement des incidents...</span>
            </div>
          ) : incidents?.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 text-lg">Aucun incident en cours</p>
              <p className="text-gray-500 text-sm mt-2">Toutes les livraisons se déroulent normalement</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Adresse
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Zone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type d'incident
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commentaire
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {incidents?.map((incident) => (
                    <tr key={incident?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {incident?.recipientName || incident?.shipment?.receiverName || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {incident?.recipientPhone || incident?.shipment?.receiverPhone || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {incident?.deliveryAddress || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {incident?.deliveryZone || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {incident?.agent?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {incident?.agent?.phone || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getIncidentTypeBadgeClass(incident?.incidentType)}>
                          {incidentTypeLabels?.[incident?.incidentType] || incident?.incidentType || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs">
                          {incident?.incidentComment || 'Aucun commentaire'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(incident?.updatedAt)?.toLocaleDateString('fr-FR')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(incident?.updatedAt)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleResolveIncident(incident?.id)}
                            disabled={actionLoading}
                            className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Repasser en À préparer"
                          >
                            <RotateCcw size={16} className="mr-1" />
                            Résoudre
                          </button>
                          <button
                            onClick={() => openReassignModal(incident)}
                            disabled={actionLoading}
                            className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Réassigner à un autre agent"
                          >
                            <UserCheck size={16} className="mr-1" />
                            Réassigner
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      {/* Reassign Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Réassigner la livraison</h3>
              <button
                onClick={closeReassignModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Client: <span className="font-semibold">{selectedIncident?.recipientName}</span>
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Adresse: <span className="font-semibold">{selectedIncident?.deliveryAddress}</span>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Agent actuel: <span className="font-semibold">{selectedIncident?.agent?.name}</span>
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner le nouvel agent
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">-- Choisir un agent --</option>
                {agents
                  ?.filter(agent => agent?.id !== selectedIncident?.agent?.id)
                  ?.map(agent => (
                    <option key={agent?.id} value={agent?.id}>
                      {agent?.name} {agent?.email ? `(${agent?.email})` : ''}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeReassignModal}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                onClick={handleReassignIncident}
                disabled={actionLoading || !selectedAgent}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {actionLoading ? (
                  <>
                    <RefreshCw className="animate-spin mr-2" size={16} />
                    Traitement...
                  </>
                ) : (
                  <>
                    <UserCheck size={16} className="mr-2" />
                    Confirmer la réassignation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}